import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit, Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { PLACEHOLDER_IMAGE } from '../../lib/placeholder';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function AdminCategories() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        image: '',
        position: 0,
        imageFile: null
    });

    const fetchCategories = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/categories`, {
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const data = new FormData();
            data.append('name', formData.name);
            data.append('slug', formData.slug);
            data.append('description', formData.description);
            data.append('position', formData.position);
            
            if (formData.imageFile) {
                data.append('image', formData.imageFile);
            } else if (editingCategory && formData.image) {
                // If editing and no new file, we can either skip or send existing URL if backend supports it
                // Usually for file uploads, if no new file is sent, the backend keeps the old one
            }

            const url = editingCategory
                ? `${API}/categories/${editingCategory.category_id}`
                : `${API}/categories`;

            const response = await fetch(url, {
                method: editingCategory ? 'PUT' : 'POST',
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include',
                body: data
            });

            if (!response.ok) throw new Error('Failed to save category');

            toast.success(editingCategory ? 'Category updated' : 'Category created');
            await fetchCategories();
            resetForm();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (categoryId) => {
        if (!window.confirm('Are you sure? This may affect products in this category.')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API}/categories/${categoryId}`, {
                method: 'DELETE',
                headers: {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include'
            });

            if (!response.ok) throw new Error('Failed to delete category');

            toast.success('Category deleted');
            await fetchCategories();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleEdit = (category) => {
        setFormData({
            name: category.name,
            slug: category.slug,
            description: category.description || '',
            image: category.image || '',
            position: category.position || 0,
            imageFile: null
        });
        setEditingCategory(category);
        setShowForm(true);
    };

    const resetForm = () => {
        setFormData({ name: '', slug: '', description: '', image: '', position: 0, imageFile: null });
        setEditingCategory(null);
        setShowForm(false);
    };

    const getImageUrl = (url) => {
        if (!url) return PLACEHOLDER_IMAGE;
        if (url.startsWith('http')) return url;
        return `${BACKEND_URL}${url}`;
    };

    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6" data-testid="admin-categories">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h1 className="font-heading text-2xl md:text-3xl text-secondary">Categories</h1>
                <div className="flex gap-4">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search categories..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 rounded-xl"
                        />
                    </div>
                    <Button
                        onClick={() => setShowForm(true)}
                        className="rounded-full bg-primary"
                        data-testid="add-category-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Category
                    </Button>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-heading text-xl text-secondary">
                                {editingCategory ? 'Edit Category' : 'Add New Category'}
                            </h2>
                            <button onClick={resetForm} className="p-2 hover:bg-surface rounded-full">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label>Category Name</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => {
                                        const name = e.target.value;
                                        setFormData({
                                            ...formData,
                                            name,
                                            slug: name.toLowerCase().replace(/\s+/g, '-')
                                        });
                                    }}
                                    required
                                    className="mt-1 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label>Slug</Label>
                                <Input
                                    value={formData.slug}
                                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                    required
                                    className="mt-1 rounded-xl"
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="mt-1 rounded-xl"
                                    rows={3}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Position</Label>
                                    <Input
                                        type="number"
                                        value={formData.position}
                                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                        className="mt-1 rounded-xl"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <Label>Category Image</Label>
                                    <Input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setFormData({ ...formData, imageFile: e.target.files[0] })}
                                        className="mt-1 rounded-xl cursor-pointer"
                                    />
                                    {editingCategory && !formData.imageFile && formData.image && (
                                        <p className="text-[10px] text-muted-foreground mt-1">Current: {formData.image.split('/').pop()}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-4 pt-4">
                                <Button type="submit" disabled={loading} className="flex-1 rounded-full bg-primary">
                                    {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                                </Button>
                                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-100 bg-surface/50">
                            <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Category</th>
                            <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Slug</th>
                            <th className="text-right py-4 px-4 font-body font-medium text-muted-foreground text-sm">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredCategories.map((cat) => (
                            <tr key={cat.category_id} className="border-b border-gray-50 hover:bg-surface/30">
                                <td className="py-4 px-4">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={getImageUrl(cat.image)}
                                            alt={cat.name}
                                            className="w-10 h-10 rounded-lg object-cover"
                                        />
                                        <span className="font-body font-medium text-secondary">{cat.name}</span>
                                    </div>
                                </td>
                                <td className="py-4 px-4">
                                    <span className="text-sm text-muted-foreground font-body">{cat.slug}</span>
                                </td>
                                <td className="py-4 px-4">
                                    <div className="flex justify-end gap-2">
                                        <button onClick={() => handleEdit(cat)} className="p-2 hover:bg-surface rounded-lg">
                                            <Edit className="w-4 h-4 text-muted-foreground" />
                                        </button>
                                        <button onClick={() => handleDelete(cat.category_id)} className="p-2 hover:bg-destructive/10 rounded-lg">
                                            <Trash2 className="w-4 h-4 text-destructive" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
